import { useEffect, useRef, useState } from 'react';
import { FeatureGate } from '../../components/FeatureGate';
import { useFeature } from '../../lib/features';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check, ChevronRight, Loader2, Trash2, Upload } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { TextField, TextArea } from '../../components/ui/TextField';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { IconButton } from '../../components/ui/IconButton';
import { AvatarView } from '../../components/ui/AvatarView';
import { AvatarImagePicker } from '../../components/ui/AvatarImagePicker';
import { Sheet } from '../../components/ui/Sheet';
import { Toggle } from '../../components/ui/Toggle';
import { AvatarSymbolPicker } from '../../components/ui/AvatarSymbolPicker';
import { Toast } from '../../components/ui/Toast';
import { AutoSaveStatus } from '../../components/ui/AutoSaveStatus';
import { useAutoSave } from '../../lib/useAutoSave';
import { AVATAR_SYMBOL_CHOICES } from '../../lib/icons';
import { fetchChannel, fetchTemplate, LimitError, type SkippedCSVRow } from '../../api/dataApi';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { CsvMessageImportSheet, CsvImportErrorBanner } from '../chat/CsvMessageImportSheet';
import {
  BUNCHING_INTERVALS,
  BUNCHING_INTERVAL_LABEL,
  CHANNEL_VIEW_DESIGNS,
  CHANNEL_VIEW_TYPES,
  CHART_VARIANTS,
  DEFAULT_BUNCHING_INTERVAL,
  DEFAULT_MEMBER_PERMISSIONS,
  label as capitalize,
  type BunchingInterval,
  type Channel,
  type MemberPermissions,
  type Template,
} from '../../types';

const AVATAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#0EA5E9'];

export function blankDraft(): Channel {
  return {
    id: '',
    title: 'New Channel',
    nickname: '@newchannel',
    description: 'Describe your channel here.',
    templateIds: [],
    viewType: 'reports',
    viewChartType: 'bar',
    viewDesign: 'classic',
    chartSettings: {},
    publicChartFiltersAllowed: false,
    // Always false for a brand new draft, matching the backend - isPublic
    // is no longer independently settable at all (see channel/create.wsl's
    // header comment) - it's exclusively controlled by enabling/disabling
    // the share link, a separate, later action (see ChannelShareScreen.tsx),
    // never available while a channel is still just a local draft.
    isPublic: false,
    accentHex: '#3B82F6',
    avatarSymbol: 'message-circle',
    avatarImageKey: '',
    followers: 0,
    following: false,
    likes: 0,
    liked: false,
    muted: false,
    posts: [],
    workflows: [],
    tags: [],
    ownerIds: [],
    memberPermissions: { ...DEFAULT_MEMBER_PERMISSIONS },
    revealRounds: false,
    bunchingInterval: DEFAULT_BUNCHING_INTERVAL,
    chartCompareChannels: [],
    reportAdditionalChannels: [],
    priority: 0,
  };
}

export function ChannelManageScreen() {
  const shareOn = useFeature('channels.share_links');
  const importOn = useFeature('channels.messages_import');
  const { channelId } = useParams();
  const isNew = !channelId;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    channel,
    channels,
    createChannel,
    updateChannel,
    deleteChannel,
    renameChannel,
    checkChannelNicknameAvailable,
    clearChannelMessages,
    isChannelOwner,
    publishChannelAsTemplate,
    profile,
  } = useStore();

  const [draft, setDraft] = useState<Channel>(blankDraft);
  // Edit is owner-only. 'checking' until we've either found the channel in
  // the store or fetched it fresh AND confirmed the caller is one of its
  // owners; 'denied' bounces to the read-only view. A brand-new channel
  // (no :channelId) is always 'granted'. This gates the whole form render
  // below so a non-owner never sees editable fields - not even during the
  // async load window, and not when the channel isn't in their store at
  // all (the old guard only redirected when it happened to be cached,
  // leaving the blank-draft form visible otherwise). update.wsl rejects a
  // non-owner 403 regardless; this is the UX half of that.
  const [access, setAccess] = useState<'checking' | 'granted' | 'denied'>(isNew ? 'granted' : 'checking');
  // Reactive mirror of draftLoadedRef - gates the auto-save hook so it can't
  // seed its baseline snapshot from the blank draft before the real channel
  // has loaded.
  const [draftLoaded, setDraftLoaded] = useState(isNew);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [limitMessage, setLimitMessage] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState<Template[]>([]);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [importResult, setImportResult] = useState('');
  const [csvSkippedRows, setCsvSkippedRows] = useState<SkippedCSVRow[]>([]);
  const [clearChatOpen, setClearChatOpen] = useState(false);
  const [clearingChat, setClearingChat] = useState(false);
  const [publishingTemplate, setPublishingTemplate] = useState(false);
  const [deleteChannelOpen, setDeleteChannelOpen] = useState(false);
  const [deletingChannel, setDeletingChannel] = useState(false);
  // Rename is a dedicated migration (POST /channel/rename/{nickname} -
  // backend internal/rename), not a field the debounced auto-save below
  // can touch - update.wsl explicitly carries the stored nickname forward
  // unchanged, so folding it into `draft` would auto-"save" with no
  // effect. Kept as its own state, seeded once the real channel loads,
  // with its own availability check and an explicit confirm step.
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [nicknameStatus, setNicknameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [renamingChannel, setRenamingChannel] = useState(false);

  // Resolve access + load the draft. Runs on a direct page load (store
  // empty / stale cache, real data lands async) and re-runs when the
  // channel list or the caller's own id changes - isChannelOwner needs
  // profile.userId, which can arrive a beat after the channel does. A
  // channel the caller doesn't have cached (never joined/followed - the
  // exact case a non-owner poking at /channels/@x/edit hits) is fetched
  // fresh so ownership can still be checked; a fetch that comes back empty
  // (private, no access) is treated as denied.
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (isNew || !channelId) return;
    if (!profile.userId) return; // wait until we know who the caller is
    let cancelled = false;

    const normalize = (c: Channel): Channel => ({
      ...c,
      ownerIds: c.ownerIds ?? [],
      memberPermissions: { ...DEFAULT_MEMBER_PERMISSIONS, ...(c.memberPermissions ?? {}) },
      revealRounds: c.revealRounds ?? false,
      bunchingInterval: c.bunchingInterval ?? DEFAULT_BUNCHING_INTERVAL,
    });
    const decide = (c: Channel | null | undefined) => {
      if (cancelled) return;
      if (c && isChannelOwner(c)) {
        setAccess('granted');
        if (!draftLoadedRef.current) {
          setDraft(normalize(c));
          setNicknameDraft(c.nickname);
          draftLoadedRef.current = true;
          setDraftLoaded(true);
        }
      } else {
        setAccess('denied');
      }
    };

    const cached = channel(channelId);
    if (cached) {
      decide(cached);
      return;
    }
    fetchChannel(channelId).then(decide);
    return () => {
      cancelled = true;
    };
  }, [channelId, isNew, channels, profile.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (access === 'denied' && channelId) {
      navigate(`/channels/${channelId}`, { replace: true });
    }
  }, [access, channelId, navigate]);

  // TemplatePickerScreen hands its result back via navigate(..., {state}) -
  // see its `done()`. Only applies it once per return trip (replace: true
  // means location.state won't linger across unrelated re-renders, but the
  // effect still keys off it explicitly rather than assuming that).
  //
  // Gated on draftLoaded: on the common path (channel already cached), this
  // effect and the draft-load effect above both fire in the same initial
  // passive-effects flush. Without this guard, the returned templateIds
  // would already be baked into `draft` by the time useAutoSave's hook first
  // sees `enabled: true` and seeds its "already persisted" baseline snapshot
  // - so the hook believed the just-picked templates were already saved and
  // never actually persisted them (they'd render fine until a refresh, then
  // silently revert). Waiting for draftLoaded means this mutation always
  // lands in a render *after* the baseline is seeded from the real loaded
  // channel, so it's correctly seen as a dirty change and debounce-saves.
  useEffect(() => {
    if (!draftLoaded) return;
    const returned = (location.state as { selectedTemplateIds?: string[] } | null)?.selectedTemplateIds;
    if (returned) setDraft((prev) => ({ ...prev, templateIds: returned }));
  }, [location.state, draftLoaded]);

  // Templates can number 100k+ (see TemplatePickerScreen's comment), so this
  // screen only ever hydrates the handful actually selected, not the full
  // index - same "list ids, fetch each in parallel" pattern used there.
  useEffect(() => {
    let cancelled = false;
    if (draft.templateIds.length === 0) {
      setSelectedTemplates([]);
      return;
    }
    Promise.all(draft.templateIds?.map((id) => fetchTemplate(id))).then((results) => {
      if (!cancelled) setSelectedTemplates(results.filter((t): t is Template => t !== null));
    });
    return () => {
      cancelled = true;
    };
  }, [draft.templateIds]);

  // The six per-member permission defaults (roadmap 4a-f). textOnly and
  // signalsOnly are opposing restrictions - turning one on forces the
  // other off, matching the backend's own mutual-exclusion rule.
  const setPerm = (key: keyof MemberPermissions, value: boolean) => {
    setDraft((prev) => {
      const next: MemberPermissions = { ...prev.memberPermissions, [key]: value };
      if (key === 'textOnly' && value) next.signalsOnly = false;
      if (key === 'signalsOnly' && value) next.textOnly = false;
      return { ...prev, memberPermissions: next };
    });
  };

  // Owner "clean chat" - deletes every message in the channel (DELETE
  // /channel/{nickname}/messages, owner-only server-side). Irreversible,
  // hence the confirm sheet below.
  const clearChat = async () => {
    setClearingChat(true);
    try {
      const deleted = await clearChannelMessages(draft.nickname);
      setClearChatOpen(false);
      setImportResult(`Cleared ${deleted} message${deleted === 1 ? '' : 's'}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to clear messages.');
    } finally {
      setClearingChat(false);
    }
  };

  // Owner "delete channel" (DELETE /channel/{nickname}, owner-only
  // server-side - see channel/delete.wsl). Unlike "Clear chat", this
  // removes the channel itself - membership, profile, everything - not
  // just its messages. Irreversible, hence its own confirm sheet; navigates
  // back to the channel list on success since there's nothing left here to
  // show.
  const removeChannel = async () => {
    setDeletingChannel(true);
    try {
      await deleteChannel(draft.id);
      navigate('/channels');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete channel.');
      setDeletingChannel(false);
    }
  };

  // "Publish as template" (roadmap #8) - snapshots this channel's attached-
  // template signal palette into a new, standalone template (not a copy of
  // this channel's messages or members - see dataApi's
  // publishChannelAsTemplate). Navigates to the new template's edit screen
  // on success, same "review/rename what you just created" pattern
  // PublicTemplateScreen's Clone button and ChannelDetailsScreen's Clone
  // button both already use.
  const publishAsTemplate = async () => {
    setPublishingTemplate(true);
    setError('');
    try {
      const created = await publishChannelAsTemplate(
        draft,
        selectedTemplates.flatMap((t) => t.signals)
      );
      navigate(`/templates/${created.id}/edit`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to publish this channel as a schema.');
    } finally {
      setPublishingTemplate(false);
    }
  };

  // Create path only - an existing channel auto-saves (below). Keeps the
  // navigate-away-on-success behavior a fresh channel needs.
  const save = async () => {
    setSaving(true);
    try {
      await createChannel(draft);
      navigate('/channels');
    } catch (err) {
      if (err instanceof LimitError) {
        setLimitMessage(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to save channel.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Edit path: debounced auto-save. A LimitError still routes to the
  // <UpgradePrompt>; anything else surfaces in the existing <Toast>.
  const auto = useAutoSave(
    draft,
    async (d: Channel) => {
      try {
        await updateChannel(d);
      } catch (err) {
        if (err instanceof LimitError) {
          setLimitMessage(err.message);
          return;
        }
        throw err;
      }
    },
    { enabled: !isNew && access === 'granted' && draftLoaded },
  );
  useEffect(() => {
    if (auto.error) setError(auto.error.message);
  }, [auto.error]);

  // Live availability check for a rename in progress (edit mode only -
  // create mode's nickname field just fails on submit, same as before).
  useEffect(() => {
    if (isNew || !draftLoaded) return;
    const next = nicknameDraft.trim();
    if (!next || next === draft.nickname) {
      setNicknameStatus('idle');
      return;
    }
    setNicknameStatus('checking');
    const timer = setTimeout(async () => {
      const available = await checkChannelNicknameAvailable(next);
      setNicknameStatus(available ? 'available' : 'taken');
    }, 400);
    return () => clearTimeout(timer);
  }, [nicknameDraft, draft.nickname, isNew, draftLoaded, checkChannelNicknameAvailable]);

  const confirmRename = async () => {
    setRenamingChannel(true);
    setError('');
    try {
      const renamed = await renameChannel(draft.id, nicknameDraft.trim());
      // Set local state directly rather than relying on the navigate()
      // below to re-seed it: React Router keeps this same component
      // instance mounted across a param-only URL change (no remount), so
      // the draftLoadedRef guard in the channel-load effect would skip
      // re-hydrating `draft`/`nicknameDraft` from the new URL - without
      // this, nicknameDraft kept showing what was just typed while
      // draft.nickname stayed the OLD value, so the availability-check
      // effect kept re-checking the (now self-taken) new name forever,
      // showing a bogus "already taken" error until a full page reload.
      setDraft(renamed);
      setNicknameDraft(renamed.nickname);
      setNicknameStatus('idle');
      navigate(`/channels/${renamed.id}/edit`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to rename channel.');
    } finally {
      setRenamingChannel(false);
    }
  };

  // Don't render the editable form until we've confirmed the caller owns
  // this channel (see `access` above). 'denied' shows nothing while the
  // redirect effect runs; 'checking' shows a bare header + spinner.
  if (access !== 'granted') {
    return (
      <div className="flex flex-col gap-5">
        <ScreenHeader title="Edit channel" />
        {access === 'checking' && (
          <div className="flex items-center gap-2 px-1 text-sm text-black/50 dark:text-white/50">
            <Loader2 size={16} className="animate-spin" />
            Loading…
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" onBlur={isNew ? undefined : auto.flush}>
      <ScreenHeader
        title={isNew ? 'Create channel' : 'Edit channel'}
        action={
          isNew ? (
            <IconButton
              icon={saving ? Loader2 : Check}
              onClick={save}
              disabled={saving}
              aria-label="Save"
              className={saving ? 'animate-spin' : ''}
            />
          ) : (
            <AutoSaveStatus status={auto.status} onRetry={auto.retry} />
          )
        }
      />

      <FormSection title="Basics">
        <TextField label="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        {isNew ? (
          <TextField label="Nickname" value={draft.nickname} onChange={(e) => setDraft({ ...draft, nickname: e.target.value })} />
        ) : (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <TextField
                  label="Nickname"
                  value={nicknameDraft}
                  onChange={(e) => setNicknameDraft(e.target.value)}
                  error={nicknameStatus === 'taken' ? 'That nickname is already taken.' : undefined}
                />
              </div>
              {nicknameDraft.trim() && nicknameDraft.trim() !== draft.nickname && (
                <Button
                  variant="bordered"
                  onClick={confirmRename}
                  disabled={nicknameStatus !== 'available' || renamingChannel}
                  loading={renamingChannel}
                >
                  Rename
                </Button>
              )}
            </div>
            {nicknameStatus === 'checking' && (
              <span className="text-xs text-black/40 dark:text-white/40">Checking availability…</span>
            )}
            {nicknameStatus === 'available' && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400">Available — click Rename to confirm.</span>
            )}
          </div>
        )}
        <TextArea
          label="Description"
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
        <Select
          label="View type"
          value={draft.viewType}
          onChange={(e) => setDraft({ ...draft, viewType: e.target.value as Channel['viewType'] })}
        >
          {CHANNEL_VIEW_TYPES?.map((t) => (
            <option key={t} value={t}>
              {capitalize(t)}
            </option>
          ))}
        </Select>
        <Select
          label="Chart type"
          value={draft.viewChartType}
          onChange={(e) => setDraft({ ...draft, viewChartType: e.target.value as Channel['viewChartType'] })}
        >
          {CHART_VARIANTS?.map((t) => (
            <option key={t} value={t}>
              {capitalize(t)}
            </option>
          ))}
        </Select>
        {/* Public share-link (/shared/{nickname}/{token}) look - see
            PublicChannelViewScreen.tsx / PublicReportDesign.tsx. Only
            matters once the channel is actually shared, but shown
            unconditionally here, same as View type/Chart type above,
            so it's set before the owner ever opens the Share screen. */}
        <Select
          label="Public page design"
          value={draft.viewDesign}
          onChange={(e) => setDraft({ ...draft, viewDesign: e.target.value as Channel['viewDesign'] })}
        >
          {CHANNEL_VIEW_DESIGNS?.map((d) => (
            <option key={d} value={d}>
              {d === 'report' ? 'Report (hero + stats)' : 'Classic'}
            </option>
          ))}
        </Select>
        {/* "Let visitors adjust chart filters" lives on the Share screen
            instead (ChannelShareScreen.tsx, alongside the other public-
            visitor switches: share link, searchable, request on join) -
            it's a visitor-facing permission, not a channel-content
            setting like the two selects above. */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <AvatarView symbol={draft.avatarSymbol} colorHex={draft.accentHex} imageKey={draft.avatarImageKey} size={72} />
            <FeatureGate feature="storage.attachments" fallback={null}>
              <div className="absolute -bottom-1 -right-1">
                <AvatarImagePicker
                  size={72}
                  onUploaded={(key) => setDraft((d) => ({ ...d, avatarImageKey: key }))}
                  onError={setError}
                />
              </div>
            </FeatureGate>
          </div>
          <Button variant="bordered" onClick={() => setShowAvatarPicker(true)}>
            Choose icon
          </Button>
        </div>
      </FormSection>

      {/* Real, server-backed membership lives on the dedicated share screen
          now (search by email/@nickname, invite, remove - see
          ChannelShareScreen.tsx) rather than this local-contact toggle
          list, which only ever wrote to Channel.members, a field the
          backend never reads or persists (see mergeChannel's comment in
          dataApi.ts). Only shown once the channel actually exists server-
          side - a brand new draft has nothing to share yet. */}
      {!isNew && shareOn && (
        <FormSection title="Members">
          <Button variant="bordered" full onClick={() => navigate(`/channels/${channelId}/share`)}>
            Share with others
          </Button>
        </FormSection>
      )}

      {/* Per-member permission defaults (roadmap 4a-f) - what a member
          added to this channel starts with. Saved as part of the channel
          on Save (POST /channel/{nickname}); a single member's overrides
          are edited on the Share screen. Editable on a brand-new draft
          too, so it applies from the first member added. */}
      <FormSection title="Member permissions">
        <span className="text-xs text-black/50 dark:text-white/50 -mb-1">
          Defaults for people you add to this channel. Adjust any one member on the Share screen.
        </span>
        <Toggle
          label="Send messages"
          checked={draft.memberPermissions.postMessages}
          onChange={(v) => setPerm('postMessages', v)}
        />
        <Toggle
          label="Send attachments"
          checked={draft.memberPermissions.uploadAttachments}
          onChange={(v) => setPerm('uploadAttachments', v)}
        />
        <Toggle
          label="Import messages from CSV"
          checked={draft.memberPermissions.importCsv}
          onChange={(v) => setPerm('importCsv', v)}
        />
        <Toggle
          label="Send reports"
          checked={draft.memberPermissions.sendReports}
          onChange={(v) => setPerm('sendReports', v)}
        />
        <Toggle
          label="Text messages only"
          checked={draft.memberPermissions.textOnly}
          onChange={(v) => setPerm('textOnly', v)}
        />
        <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
          Members can't tap signals - plain text only.
        </span>
        <Toggle
          label="Signals only"
          checked={draft.memberPermissions.signalsOnly}
          onChange={(v) => setPerm('signalsOnly', v)}
        />
        <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
          Members can only tap signals - no free text.
        </span>
      </FormSection>

      <FeatureGate feature="channels.reveal_rounds" fallback={null}>
      <FormSection title="Reveal rounds">
        <Toggle
          label="Enable reveal rounds"
          checked={draft.revealRounds}
          onChange={(v) => setDraft((prev) => ({ ...prev, revealRounds: v }))}
        />
        <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
          Adds an owner control to the chat header: start a round, let members
          answer secretly, then reveal every answer at once (planning poker,
          voting, games).
        </span>
      </FormSection>
      </FeatureGate>

      <FormSection title="Message bunching">
        <Select
          label="Bunch messages sent within"
          value={String(draft.bunchingInterval)}
          onChange={(e) => setDraft((prev) => ({ ...prev, bunchingInterval: Number(e.target.value) as BunchingInterval }))}
        >
          {BUNCHING_INTERVALS.map((i) => (
            <option key={i} value={i}>
              {BUNCHING_INTERVAL_LABEL[i]}
            </option>
          ))}
        </Select>
        <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
          Consecutive messages from the same sender in this channel's chat,
          sent within this window, stack together instead of showing as
          separate bubbles.
        </span>
      </FormSection>

      {/* Owner-only, both here (this section is hidden from anyone else)
          and for real, server-side - see CSVMessageImportSheet's comment. */}
      {!isNew && isChannelOwner(draft) && (
        <FormSection title="Messages">
          {importOn && (
            <Button variant="bordered" full onClick={() => setCsvImportOpen(true)}>
              <Upload size={16} />
              Import from CSV
            </Button>
          )}
          <Button variant="destructive" full onClick={() => setClearChatOpen(true)}>
            <Trash2 size={16} />
            Clear chat
          </Button>
          <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
            Permanently deletes every message in this channel. This can't be undone.
          </span>
        </FormSection>
      )}

      {/* Owner-only, both here and for real, server-side - see
          channel/delete.wsl's owner check. Kept as its own section below
          Messages rather than folded into it: clearing chat and deleting
          the whole channel are very different blast radii and shouldn't
          share a heading. */}
      {!isNew && isChannelOwner(draft) && (
        <FormSection title="Danger zone">
          <Button variant="destructive" full onClick={() => setDeleteChannelOpen(true)}>
            <Trash2 size={16} />
            Delete channel
          </Button>
          <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
            Permanently deletes this channel for everyone - messages, members, everything. This can't be undone.
          </span>
        </FormSection>
      )}

      <FormSection title="Schemas">
        <button
          onClick={() =>
            navigate('/templates/pick', { state: { selected: draft.templateIds, returnTo: location.pathname } })
          }
          className="w-full flex items-center gap-3 text-left"
        >
          <div className="flex-1 min-w-0">
            {selectedTemplates.length === 0 ? (
              <span className="text-sm text-black/40 dark:text-white/40">No schemas selected</span>
            ) : (
              <span className="text-sm truncate block">
                {selectedTemplates?.map((t) => t.title).join(', ')}
              </span>
            )}
          </div>
          <ChevronRight size={18} className="text-black/30 dark:text-white/30 shrink-0" />
        </button>
        {!isNew && selectedTemplates.length > 0 && (
          <>
            <Button variant="bordered" full onClick={publishAsTemplate} loading={publishingTemplate} disabled={publishingTemplate}>
              Publish as schema
            </Button>
            <span className="text-xs text-black/50 dark:text-white/50 -mt-2">
              Creates a new, private schema from this channel's attached signal presets above - not a copy of
              its messages or members.
            </span>
          </>
        )}
      </FormSection>

      <Sheet open={showAvatarPicker} onClose={() => setShowAvatarPicker(false)} title="Choose icon">
        <AvatarSymbolPicker
          symbols={AVATAR_SYMBOL_CHOICES}
          colors={AVATAR_COLORS}
          onPick={(symbol, colorHex) => {
            setDraft((d) => ({ ...d, avatarSymbol: symbol, accentHex: colorHex }));
            setShowAvatarPicker(false);
          }}
        />
      </Sheet>

      {!isNew && (
        <CsvMessageImportSheet
          open={csvImportOpen}
          onClose={() => setCsvImportOpen(false)}
          channelNickname={channelId!}
          availableSignals={selectedTemplates.flatMap((t) => t.signals)}
          onImported={(result) => {
            setImportResult(
              `Imported ${result.importedCount} message${result.importedCount === 1 ? '' : 's'}${
                result.skippedCount > 0 ? ` (${result.skippedCount} skipped)` : ''
              }.`
            );
            setCsvSkippedRows(result.skippedRows);
          }}
        />
      )}

      <Sheet
        open={clearChatOpen}
        onClose={() => {
          if (!clearingChat) setClearChatOpen(false);
        }}
        title="Clear this channel's chat?"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-black/70 dark:text-white/70">
            Every message in <span className="font-semibold">{draft.title}</span> will be permanently
            deleted for everyone. This can't be undone.
          </p>
          <div className="flex gap-2">
            <Button variant="bordered" full disabled={clearingChat} onClick={() => setClearChatOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" full loading={clearingChat} disabled={clearingChat} onClick={clearChat}>
              Clear chat
            </Button>
          </div>
        </div>
      </Sheet>

      <Sheet
        open={deleteChannelOpen}
        onClose={() => {
          if (!deletingChannel) setDeleteChannelOpen(false);
        }}
        title="Delete this channel?"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-black/70 dark:text-white/70">
            <span className="font-semibold">{draft.title}</span> will be permanently deleted for everyone -
            messages, members, everything. This can't be undone.
          </p>
          <div className="flex gap-2">
            <Button variant="bordered" full disabled={deletingChannel} onClick={() => setDeleteChannelOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" full loading={deletingChannel} disabled={deletingChannel} onClick={removeChannel}>
              Delete channel
            </Button>
          </div>
        </div>
      </Sheet>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      {importResult && <Toast message={importResult} onDismiss={() => setImportResult('')} />}
      <CsvImportErrorBanner rows={csvSkippedRows} onDismiss={() => setCsvSkippedRows([])} />
      <UpgradePrompt
        open={!!limitMessage}
        onClose={() => setLimitMessage('')}
        message={limitMessage}
        title="Channel limit reached"
      />
    </div>
  );
}
